import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Crown, Loader2, ShieldCheck, Zap, Settings } from "lucide-react";
import { PlanComparison } from "@/components/PlanComparison";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";

const PlanCard = ({ type, price, per, badge, save, features, onSelect, loading, t, highlight }) => (
  <div data-testid={`pricing-plan-card-${type}`}
    className={`relative rounded-2xl border bg-white p-6 ${highlight ? "border-emerald-400 shadow-lg ring-1 ring-emerald-200" : "border-gray-200"}`}>
    {badge && <span className="absolute -top-3 right-5 rounded-full bg-amber-400 px-3 py-1 text-xs font-bold text-white">{badge}</span>}
    <p className="text-sm font-bold uppercase tracking-wider text-gray-400">{t(`pricing.${type}`)}</p>
    <div className="mt-2 flex flex-wrap items-end gap-x-2 gap-y-1">
      <span className="text-4xl font-extrabold text-gray-900">${price}</span>
      <span className="mb-1 text-sm text-gray-500">{per}</span>
      {save && <span data-testid="pricing-save-badge" className="mb-1 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-bold text-emerald-700">{save}</span>}
    </div>
    <ul className="mt-5 space-y-2.5">
      {features.map((f, i) => (
        <li key={i} className="flex items-start gap-2 text-sm text-gray-600"><Check size={16} className="mt-0.5 shrink-0 text-emerald-500" /> {f}</li>
      ))}
    </ul>
    <button data-testid={`pricing-subscribe-button-${type}`} onClick={() => onSelect(type)} disabled={loading}
      className={`mt-6 flex w-full items-center justify-center gap-2 rounded-full py-3.5 text-sm font-bold transition-colors disabled:opacity-60 ${highlight ? "bg-emerald-500 text-white hover:bg-emerald-600" : "border border-emerald-500 text-emerald-600 hover:bg-emerald-50"}`}>
      {loading ? <Loader2 size={16} className="animate-spin" /> : <Crown size={16} />} {t("pricing.subscribe")}
    </button>
  </div>
);

export default function Pricing() {
  const { t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState("");
  const [portalLoading, setPortalLoading] = useState(false);

  const subscribe = async (plan) => {
    if (!user) { navigate("/login"); return; }
    setLoading(plan);
    try {
      const { data } = await api.post("/payments/checkout", { plan, origin_url: window.location.origin });
      toast.message(t("pricing.redirect"));
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiError(e)); setLoading(""); }
  };

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await api.post("/payments/portal", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(apiError(e)); setPortalLoading(false); }
  };

  const featsMonthly = [t("plan.feat.pubmedStudies"), t("plan.feat.condition"), t("plan.feat.expertDose")];
  const featsYearly = [t("plan.feat.pubmedStudies"), t("plan.feat.condition"), t("plan.feat.formula"), t("plan.feat.expertDose")];

  return (
    <div className="mx-auto max-w-4xl">
      <div className="mb-8 text-center">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-100 text-amber-500"><Crown size={28} /></div>
        <h1 className="text-3xl font-extrabold tracking-tight text-gray-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{t("pricing.title")}</h1>
        <p className="mx-auto mt-2 max-w-lg text-sm text-gray-600">{t("pricing.sub")}</p>
      </div>

      {user?.is_pro ? (
        <div className="mb-8 rounded-2xl border border-emerald-200 bg-emerald-50 p-6 text-center" data-testid="pricing-current-premium">
          <ShieldCheck className="mx-auto mb-2 text-emerald-500" size={32} />
          <p className="text-lg font-bold text-emerald-800">{t("pricing.current")}</p>
          <p className="mx-auto mt-1 max-w-sm text-sm text-emerald-700">{t("pricing.manageSub")}</p>
          <button data-testid="pricing-manage-button" onClick={openPortal} disabled={portalLoading}
            className="mt-4 inline-flex items-center gap-2 rounded-full bg-emerald-500 px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-emerald-600 disabled:opacity-60">
            {portalLoading ? <Loader2 size={16} className="animate-spin" /> : <Settings size={16} />} {t("pricing.manage")}
          </button>
        </div>
      ) : (
        <div className="mb-8 grid gap-5 sm:grid-cols-2">
          <PlanCard type="monthly" price="9.99" per={t("pricing.perMonth")} features={featsMonthly} onSelect={subscribe} loading={loading === "monthly"} t={t} />
          <PlanCard type="yearly" price="59.99" per={t("pricing.perYear")} badge={t("pricing.bestValue")} save={t("pricing.save")} features={featsYearly} onSelect={subscribe} loading={loading === "yearly"} t={t} highlight />
        </div>
      )}

      <PlanComparison />

      <p className="mt-5 flex items-center justify-center gap-1.5 text-xs text-gray-400">
        <Zap size={13} /> {t("pricing.securedBy")}
      </p>
    </div>
  );
}
